/**
 * Helper functions to check billing status of course lessons.
 * A lesson is considered "billed" when billingDate is not null/undefined.
 */

import React from "react";
import {Modal, Collapse} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import i18n from "../../../services/i18n";

const {Panel} = Collapse;

interface LessonWithBillingDate {
    billingDate?: Date | string | null;
}

interface LessonWithIdAndBillingDate extends LessonWithBillingDate {
    id?: string;
}

/**
 * Checks if a single lesson has been billed (has a billingDate set)
 */
export const isLessonBilled = (lesson: LessonWithBillingDate): boolean => {
    return lesson.billingDate !== null && lesson.billingDate !== undefined;
};

/**
 * Checks if any lesson in the array has been billed
 */
export const hasAnyBilledLessons = (lessons: LessonWithBillingDate[]): boolean => {
    if (!lessons || lessons.length === 0) {
        return false;
    }
    return lessons.some(isLessonBilled);
};

/**
 * Returns an array of lesson IDs that have been billed
 */
export const getBilledLessonIds = (lessons: LessonWithIdAndBillingDate[]): string[] => {
    if (!lessons || lessons.length === 0) {
        return [];
    }
    return lessons
        .filter(isLessonBilled)
        .map((lesson) => lesson.id)
        .filter((id): id is string => id !== undefined && id !== null);
};

/**
 * Returns the count of billed lessons
 */
export const getBilledLessonCount = (lessons: LessonWithBillingDate[]): number => {
    if (!lessons || lessons.length === 0) {
        return 0;
    }
    return lessons.filter(isLessonBilled).length;
};

/**
 * Shows a standardized error modal for billing lock violations.
 * Cleans the backend error message and displays it with helpful hints.
 */
export const showBillingLockErrorModal = (errorMessage: string): void => {
    const errorMessageCleaned = errorMessage
        .replace(/Bad Request:\s*(updateCourse|updateCourseAndBookings):\s*Error:\s*/g, "")
        .replace(/Bad Request:\s*/g, "")
        .trim();

    const isDeleteError = errorMessage.includes("gelöscht");

    const title = isDeleteError ? i18n.containers.courses.CourseEdit.billedLessons.errorModal.titleDeleted() : i18n.containers.courses.CourseEdit.billedLessons.errorModal.titleChanged();

    Modal.error({
        title,
        content: React.createElement(
            "div",
            null,
            React.createElement("p", null, errorMessageCleaned),
            React.createElement("p", {style: {marginTop: "1rem", fontSize: "0.9rem", color: "#666"}}, i18n.containers.courses.CourseEdit.billedLessons.errorModal.protectedInfo()),
            React.createElement(
                Collapse,
                {
                    style: {marginTop: "1rem"},
                    bordered: false,
                    expandIcon: ({isActive}) =>
                        React.createElement(InfoCircleOutlined, {
                            style: {fontSize: "14px"},
                            rotate: isActive ? 90 : 0,
                        }),
                },
                React.createElement(
                    Panel,
                    {
                        header: i18n.containers.courses.CourseEdit.billedLessons.errorModal.hintsHeader(),
                        key: "1",
                        style: {
                            background: "#f5f5f5",
                            borderRadius: "4px",
                            border: "1px solid #d9d9d9",
                        },
                    },
                    React.createElement(
                        "div",
                        {style: {fontSize: "0.85rem", color: "#595959"}},
                        React.createElement(
                            "ul",
                            {style: {margin: 0, paddingLeft: "20px"}},
                            React.createElement("li", {style: {marginBottom: "0.5rem"}}, i18n.containers.courses.CourseEdit.billedLessons.errorModal.hintResetButton()),
                            React.createElement("li", null, i18n.containers.courses.CourseEdit.billedLessons.errorModal.hintReverseBilling()),
                        ),
                    ),
                ),
            ),
        ),
        okText: i18n.containers.courses.CourseEdit.billedLessons.errorModal.okText(),
        width: 500,
    });
};
